/**
 * CreateLargeBondModal - Modal shell for creating, editing, and cloning large bonds
 * Uses useReducer with section components following AcceptQuoteForm pattern
 */

import { useReducer, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  OathModal,
  OathButton,
  OathAlert,
  OathSpinner,
} from '../../shared/OathUI';
import {
  createLargeBond,
  updateLargeBond,
  getLargeBondDetails,
} from '../../../services/oathLargeBondService';

import BondInfoSection from './form/BondInfoSection';
import DefendantSection from './form/DefendantSection';
import CitizenshipSection from './form/CitizenshipSection';
import BondConditionsSection from './form/BondConditionsSection';
import IndemnitorSection from './form/IndemnitorSection';
import OffenseSection from './form/OffenseSection';
import PowerInfoSection from './form/PowerInfoSection';
import BondDocumentUpload from './form/BondDocumentUpload';
import AdditionalDetailsSection from './form/AdditionalDetailsSection';
import {
  isRepeatableCollateralType,
  collateralTypeLabel,
  LARGE_BOND_FIELD_LIMITS as LIMITS,
  fieldTooLongMessage,
} from './shared/largeBondConstants';

// Default empty indemnitor template
const EMPTY_COLLATERAL = {
  collateralType: '',
  collateralAmount: '',
  liens: '',
  collateralStreet1: '',
  collateralStreet2: '',
  collateralCity: '',
  collateralState: '',
  collateralZip: '',
};

const EMPTY_INDEMNITOR = {
  relationship: '',
  firstName: '',
  lastName: '',
  occupation: '',
  durationOfEmployment: '',
  collaterals: [{ ...EMPTY_COLLATERAL }],
};

const INITIAL_STATE = {
  // Bond info
  bondAmount: '',
  requestedBy: '',
  callbackPhone: '',
  // Defendant
  firstName: '',
  middleInitial: '',
  lastName: '',
  dob: '',
  age: '',
  ssn: '',
  occupation: '',
  income: '',
  street1: '',
  street2: '',
  city: '',
  state: '',
  zip: '',
  // Citizenship
  isUsCitizen: true,
  countryOfOrigin: '',
  immigrationStatus: '',
  citizenDescription: '',
  isTransferBond: false,
  tbondOriginDescription: '',
  // Bond conditions
  courtOrderedMonitor: false,
  agentRequiredMonitor: false,
  pretrialIntervention: false,
  drugTesting: false,
  conditionDescription: '',
  checkInType: '',
  checkInFrequency: '',
  // Indemnitors
  indemnitors: [{ ...EMPTY_INDEMNITOR, collaterals: [{ ...EMPTY_COLLATERAL }] }],
  // Offense
  offense: '',
  // Powers (managed via API for approved bonds)
  powers: [],
  // Additional
  additionalDetails: '',
};

function formReducer(state, action) {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };

    case 'UPDATE_INDEMNITOR': {
      const indemnitors = state.indemnitors.map((ind, i) =>
        i === action.index ? { ...ind, [action.field]: action.value } : ind
      );
      return { ...state, indemnitors };
    }

    case 'ADD_INDEMNITOR':
      return {
        ...state,
        indemnitors: [...state.indemnitors, { ...EMPTY_INDEMNITOR, collaterals: [{ ...EMPTY_COLLATERAL }] }],
      };

    case 'REMOVE_INDEMNITOR':
      return {
        ...state,
        indemnitors: state.indemnitors.filter((_, i) => i !== action.index),
      };

    case 'UPDATE_COLLATERAL': {
      const indemnitors = state.indemnitors.map((ind, i) => {
        if (i !== action.indemnitorIndex) return ind;
        const collaterals = ind.collaterals.map((coll, ci) =>
          ci === action.collateralIndex ? { ...coll, [action.field]: action.value } : coll
        );
        return { ...ind, collaterals };
      });
      return { ...state, indemnitors };
    }

    case 'ADD_COLLATERAL': {
      const indemnitors = state.indemnitors.map((ind, i) => {
        if (i !== action.indemnitorIndex) return ind;
        return { ...ind, collaterals: [...ind.collaterals, { ...EMPTY_COLLATERAL }] };
      });
      return { ...state, indemnitors };
    }

    case 'REMOVE_COLLATERAL': {
      const indemnitors = state.indemnitors.map((ind, i) => {
        if (i !== action.indemnitorIndex) return ind;
        return { ...ind, collaterals: ind.collaterals.filter((_, ci) => ci !== action.collateralIndex) };
      });
      return { ...state, indemnitors };
    }

    case 'SET_POWERS':
      return { ...state, powers: action.powers };

    case 'ADD_POWER':
      return { ...state, powers: [...state.powers, action.power] };

    case 'REMOVE_POWER':
      return { ...state, powers: state.powers.filter((p) => p.largeBondCourtInfoId !== action.powerId) };

    case 'INIT':
      return { ...INITIAL_STATE, ...action.payload };

    case 'RESET':
      return { ...INITIAL_STATE, indemnitors: [{ ...EMPTY_INDEMNITOR, collaterals: [{ ...EMPTY_COLLATERAL }] }] };

    default:
      return state;
  }
}

const parseCurrency = (val) =>
  parseFloat(String(val).replace(/[^\d.]/g, '')) || 0;

const areRequiredFieldsComplete = (s) =>
  !!s.bondAmount &&
  !!s.requestedBy.trim() &&
  !!s.callbackPhone.trim() &&
  !!s.firstName.trim() &&
  !!s.lastName.trim() &&
  (!!s.dob || !!s.age?.trim()) &&
  !!s.offense.trim() &&
  s.indemnitors.length > 0 &&
  s.indemnitors.every((ind) =>
    !!ind.relationship.trim() &&
    ind.collaterals.every((c) =>
      !!c.collateralType &&
      (c.collateralType !== 'CH' || !!c.collateralAmount) &&
      (c.collateralType !== 'DE' || (!!c.collateralStreet1 && !!c.collateralCity && !!c.collateralState && !!c.collateralZip))
    )
  );

/**
 * @param {string} mode - 'create' | 'edit' | 'clone'
 * @param {string|number} bondId - Bond ID for edit/clone modes
 * @param {string|number} agencyId - Required for create/update API calls
 */
const CreateLargeBondModal = ({ isOpen, onClose, onSaved, mode = 'create', bondId, agencyId }) => {
  const [formState, dispatch] = useReducer(formReducer, INITIAL_STATE);
  const [validationError, setValidationError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [existingDetails, setExistingDetails] = useState('');
  const [bondStatus, setBondStatus] = useState('');
  const [successInfo, setSuccessInfo] = useState(null);
  const initializedRef = useRef(false);

  const isEdit = mode === 'edit';
  const isClone = mode === 'clone';
  const POWER_ELIGIBLE_STATUSES = ['AP', 'FP', 'FP1', 'FP2', 'FP3'];
  const isPowerEligible = POWER_ELIGIBLE_STATUSES.includes(bondStatus) || bondStatus?.startsWith('FP');
  const isPending = bondStatus === 'PD';
  const isApproved = bondStatus === 'AP';
  const isClosed = ['NW', 'DL', 'WWA', 'UAP', 'FC', 'CFD'].includes(bondStatus);
  const formDisabled = (isPowerEligible || isClosed) && isEdit;

  const title = isEdit ? 'Edit Large Bond' : isClone ? 'Clone Large Bond' : 'Create Large Bond';

  // Load bond data for edit/clone
  useEffect(() => {
    if (!isOpen || !bondId || initializedRef.current) return;
    if (mode === 'create') {
      initializedRef.current = true;
      return;
    }

    initializedRef.current = true;
    setIsLoading(true);

    getLargeBondDetails(bondId).then((result) => {
      if (!result.success) {
        toast.error('Failed to load bond details');
        setIsLoading(false);
        return;
      }

      const data = result.data;
      setBondStatus(data.status || '');
      setExistingDetails(data.additionalDetails || '');

      const indemnitors = Array.isArray(data.indemnitors) && data.indemnitors.length > 0
        ? data.indemnitors.map((ind) => ({
            relationship: ind.relationshipToDefendant || '',
            firstName: ind.firstName || '',
            lastName: ind.lastName || '',
            occupation: ind.occupation || '',
            durationOfEmployment: ind.durationOfEmployment || '',
            collaterals: Array.isArray(ind.collaterals) && ind.collaterals.length > 0
              ? ind.collaterals.map((c) => ({
                  collateralType: c.collateralType || '',
                  collateralAmount: c.collateralAmount != null ? String(c.collateralAmount) : '',
                  liens: c.liens || '',
                  collateralStreet1: c.collateralStreet1 || '',
                  collateralStreet2: c.collateralStreet2 || '',
                  collateralCity: c.collateralCity || '',
                  collateralState: c.collateralState || '',
                  collateralZip: c.collateralZip || '',
                }))
              : [{ ...EMPTY_COLLATERAL }],
          }))
        : [{ ...EMPTY_INDEMNITOR, collaterals: [{ ...EMPTY_COLLATERAL }] }];

      dispatch({
        type: 'INIT',
        payload: {
          bondAmount: data.amount != null ? String(data.amount) : '',
          requestedBy: data.requestedBy || '',
          callbackPhone: data.callbackPhone || '',
          firstName: data.firstName || '',
          middleInitial: data.middleInitial || '',
          lastName: data.lastName || '',
          dob: data.dateOfBirth?.split('T')[0] || '',
          age: data.age ? String(data.age) : '',
          ssn: data.ssn ? data.ssn.replace(/(\d{3})(\d{2})(\d{1,4})/, '$1-$2-$3') : '',
          occupation: data.occupation || '',
          income: data.income != null ? String(data.income) : '',
          street1: data.street1 || '',
          street2: data.street2 || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          isUsCitizen: data.isUsCitizen ?? true,
          countryOfOrigin: data.countryOfOrigin || '',
          immigrationStatus: data.immigrationStatus || '',
          citizenDescription: data.citizenDescription || data.description || '',
          isTransferBond: data.isTransferBond ?? false,
          tbondOriginDescription: data.tbondOriginDescription || '',
          courtOrderedMonitor: data.courtOrderedMonitor || false,
          agentRequiredMonitor: data.agentRequiredMonitor || false,
          pretrialIntervention: data.pretrialIntervention || false,
          drugTesting: data.drugTesting || false,
          conditionDescription: data.conditionDescription || '',
          checkInType: data.checkInType || '',
          checkInFrequency: data.checkInFrequency || '',
          offense: data.offense || '',
          powers: isClone ? [] : (Array.isArray(data.powers) ? data.powers : []),
          additionalDetails: '',
          indemnitors,
        },
      });

      setIsLoading(false);
    });
  }, [isOpen, bondId, mode, isClone]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      initializedRef.current = false;
      dispatch({ type: 'RESET' });
      setValidationError('');
      setBondStatus('');
      setExistingDetails('');
    }
  }, [isOpen]);

  // Validation
  const validate = () => {
    if (!formState.bondAmount || parseCurrency(formState.bondAmount) <= 0) {
      setValidationError('Bond Amount is required.');
      return false;
    }
    if (!formState.requestedBy.trim()) {
      setValidationError('Requested By is required.');
      return false;
    }
    if (!formState.callbackPhone.trim()) {
      setValidationError('Callback Phone is required.');
      return false;
    }
    if (!formState.firstName.trim()) {
      setValidationError('Defendant First Name is required.');
      return false;
    }
    if (!formState.lastName.trim()) {
      setValidationError('Defendant Last Name is required.');
      return false;
    }
    if (!formState.dob && !formState.age?.trim()) {
      setValidationError('Defendant Date of Birth or Age is required.');
      return false;
    }

    // Citizenship validation
    if (formState.isUsCitizen === false) {
      if (!formState.countryOfOrigin.trim()) {
        setValidationError('Country of Origin is required for non-US citizens.');
        return false;
      }
      if (!formState.immigrationStatus.trim()) {
        setValidationError('Immigration Status is required for non-US citizens.');
        return false;
      }
    }

    if (!formState.offense.trim()) {
      setValidationError('Offense / Charge is required.');
      return false;
    }

    // Transfer bond validation
    if (formState.isTransferBond === true && !formState.tbondOriginDescription.trim()) {
      setValidationError('Transfer Bond Origin Description is required.');
      return false;
    }

    // Indemnitor/collateral validation
    // Within one indemnitor, only Property Deeds may repeat; every other
    // collateral type is limited to one per indemnitor. Different indemnitors
    // may each have their own Cash / Indemnity Agreement.
    for (let i = 0; i < formState.indemnitors.length; i++) {
      const ind = formState.indemnitors[i];
      if (!ind.relationship.trim()) {
        setValidationError(`Indemnitor ${i + 1}: Indemnitor relationship is required.`);
        return false;
      }
      const firstUseOfType = {};
      for (let j = 0; j < ind.collaterals.length; j++) {
        const c = ind.collaterals[j];
        if (!c.collateralType) {
          setValidationError(`Indemnitor ${i + 1} - Collateral ${j + 1}: Collateral Type is required.`);
          return false;
        }
        if (!isRepeatableCollateralType(c.collateralType)) {
          const priorIndex = firstUseOfType[c.collateralType];
          if (priorIndex !== undefined) {
            setValidationError(
              `Indemnitor ${i + 1} - Collateral ${j + 1}: Only one ${collateralTypeLabel(c.collateralType)} collateral is allowed per indemnitor ` +
              `(already entered as Collateral ${priorIndex + 1}). Only Property Deeds can be added more than once.`
            );
            return false;
          }
          firstUseOfType[c.collateralType] = j;
        }
        if (c.collateralType === 'CH' && !c.collateralAmount) {
          setValidationError(`Indemnitor ${i + 1} - Collateral ${j + 1}: Amount is required for Cash collateral.`);
          return false;
        }
        if (c.collateralType === 'DE') {
          if (!c.collateralStreet1 || !c.collateralCity || !c.collateralState || !c.collateralZip) {
            setValidationError(`Indemnitor ${i + 1} - Collateral ${j + 1}: Collateral address (Street, City, State, Zip) is required for Deed.`);
            return false;
          }
          if (!/^\d{5}$/.test(c.collateralZip)) {
            setValidationError(`Indemnitor ${i + 1} - Collateral ${j + 1}: Collateral Zip must be a 5-digit number.`);
            return false;
          }
        }
      }
    }

    // Length limits — backstop for pasted text or older prefilled edit data
    // that slipped past maxLength. Mirrors the API's column checks exactly.
    const tooLong = (label, value, limit) => {
      const len = (value || '').trim().length;
      if (len > limit) {
        setValidationError(fieldTooLongMessage(label, len, limit));
        return true;
      }
      return false;
    };
    if (tooLong('Charges / Offense description', formState.offense, LIMITS.OFFENSE)) return false;
    if (tooLong('Requested By', formState.requestedBy, LIMITS.REQUESTED_BY)) return false;
    if (tooLong('Callback Phone', formState.callbackPhone, LIMITS.CALLBACK_PHONE)) return false;
    if (tooLong('Defendant First Name', formState.firstName, LIMITS.FIRST_NAME)) return false;
    if (tooLong('Defendant Middle Initial', formState.middleInitial, LIMITS.MIDDLE_INITIAL)) return false;
    if (tooLong('Defendant Last Name', formState.lastName, LIMITS.LAST_NAME)) return false;
    if (tooLong('Defendant SSN', formState.ssn.replace(/\D/g, ''), LIMITS.SSN)) return false;
    if (tooLong('Defendant Occupation', formState.occupation, LIMITS.OCCUPATION)) return false;
    if (tooLong('Defendant Street 1', formState.street1, LIMITS.STREET1)) return false;
    if (tooLong('Defendant Street 2', formState.street2, LIMITS.STREET2)) return false;
    if (tooLong('Defendant City', formState.city, LIMITS.CITY)) return false;
    if (tooLong('Defendant State', formState.state, LIMITS.STATE)) return false;
    if (tooLong('Defendant Zip', formState.zip, LIMITS.ZIP)) return false;
    for (let i = 0; i < formState.indemnitors.length; i++) {
      const ind = formState.indemnitors[i];
      const p = `Indemnitor ${i + 1}`;
      if (tooLong(`${p} First Name`, ind.firstName, LIMITS.INDEMNITOR_FIRST_NAME)) return false;
      if (tooLong(`${p} Last Name`, ind.lastName, LIMITS.INDEMNITOR_LAST_NAME)) return false;
      if (tooLong(`${p} Relationship to Defendant`, ind.relationship, LIMITS.INDEMNITOR_RELATIONSHIP)) return false;
      if (tooLong(`${p} Occupation`, ind.occupation, LIMITS.INDEMNITOR_OCCUPATION)) return false;
      for (let j = 0; j < ind.collaterals.length; j++) {
        const c = ind.collaterals[j];
        const cp = `${p} - Collateral ${j + 1}`;
        if (tooLong(`${cp} Street 1`, c.collateralStreet1, LIMITS.COLLATERAL_STREET1)) return false;
        if (tooLong(`${cp} Street 2`, c.collateralStreet2, LIMITS.COLLATERAL_STREET2)) return false;
        if (tooLong(`${cp} City`, c.collateralCity, LIMITS.COLLATERAL_CITY)) return false;
        if (tooLong(`${cp} State`, c.collateralState, LIMITS.COLLATERAL_STATE)) return false;
        if (tooLong(`${cp} Zip`, c.collateralZip, LIMITS.COLLATERAL_ZIP)) return false;
      }
    }

    setValidationError('');
    return true;
  };

  // Submit
  const handleSubmit = async () => {
    if (isSubmitting) return;
    if (!validate()) return;

    setIsSubmitting(true);

    const payload = {
      BondAmount: parseCurrency(formState.bondAmount),
      RequestedBy: formState.requestedBy.trim(),
      CallbackPhone: formState.callbackPhone.trim(),
      FirstName: formState.firstName.trim(),
      MiddleInitial: formState.middleInitial.trim(),
      LastName: formState.lastName.trim(),
      DateOfBirth: formState.dob || null,
      Age: formState.age || null,
      Ssn: formState.ssn.replace(/\D/g, ''),
      Occupation: formState.occupation.trim(),
      Income: formState.income ? String(parseCurrency(formState.income)) : null,
      Street1: formState.street1.trim(),
      Street2: formState.street2.trim(),
      City: formState.city.trim(),
      State: formState.state,
      Zip: formState.zip.trim(),
      IsUsCitizen: formState.isUsCitizen,
      CountryOfOrigin: formState.countryOfOrigin.trim(),
      ImmigrationStatus: formState.immigrationStatus.trim(),
      CitizenDescription: formState.citizenDescription.trim(),
      IsTransferBond: formState.isTransferBond,
      TbondOriginDescription: formState.tbondOriginDescription.trim(),
      CourtOrderedMonitor: formState.courtOrderedMonitor,
      AgentRequiredMonitor: formState.agentRequiredMonitor,
      PretrialIntervention: formState.pretrialIntervention,
      DrugTesting: formState.drugTesting,
      CheckInType: formState.checkInType,
      CheckInFrequency: formState.checkInFrequency.trim(),
      ConditionDescription: formState.conditionDescription.trim(),
      Offense: formState.offense.trim(),
      AdditionalDetails: formState.additionalDetails.trim(),
      IsOnline: true,
      Indemnitors: formState.indemnitors.map((ind) => ({
        FirstName: ind.firstName.trim(),
        LastName: ind.lastName.trim(),
        Occupation: ind.occupation.trim(),
        DurationOfEmployment: ind.durationOfEmployment.trim(),
        RelationshipToDefendant: ind.relationship.trim(),
        Collaterals: ind.collaterals.map((c) => ({
          CollateralType: c.collateralType,
          CollateralAmount: c.collateralAmount ? String(parseCurrency(c.collateralAmount)) : '0',
          Liens: c.liens.trim(),
          CollateralStreet1: c.collateralStreet1.trim(),
          CollateralStreet2: c.collateralStreet2.trim(),
          CollateralCity: c.collateralCity.trim(),
          CollateralState: c.collateralState,
          CollateralZip: c.collateralZip.trim(),
        })),
      })),
    };

    try {
      let result;
      if (isEdit) {
        result = await updateLargeBond(bondId, agencyId, payload);
      } else {
        result = await createLargeBond(agencyId, payload);
      }

      if (!result.success) {
        // The API's 400 message names the field and the overage — show it on
        // the form (not just a toast) so the agent can act on it.
        const message = result.error || 'Failed to save bond';
        setValidationError(message);
        toast.error(message);
        setIsSubmitting(false);
        return;
      }

      const savedBond = result.data;
      if (isEdit) {
        toast.success(`Bond ${savedBond?.refNumber || ''} updated successfully!`);
        onSaved?.();
        onClose();
      } else {
        setSuccessInfo({ refNumber: savedBond?.refNumber || '' });
      }
    } catch {
      toast.error('Error saving bond. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  // Is this collateral type already used by a DIFFERENT collateral on the SAME
  // indemnitor? Deeds are exempt. Other indemnitors may use the same type.
  // Used by IndemnitorSection to hide taken types from the picker and to flag
  // duplicates that arrive via edit data.
  const isCollateralTypeTaken = (type, indemnitorIndex, collateralIndex) => {
    if (!type || isRepeatableCollateralType(type)) return false;
    const ind = formState.indemnitors[indemnitorIndex];
    if (!ind) return false;
    return ind.collaterals.some((c, j) => c.collateralType === type && j !== collateralIndex);
  };

  // Defendant address for "Same as Defendant" button in IndemnitorSection
  const defendantAddress = {
    street1: formState.street1,
    street2: formState.street2,
    city: formState.city,
    state: formState.state,
    zip: formState.zip,
  };

  // Success confirmation after creating a new large bond
  if (successInfo) {
    return (
      <OathModal
        isOpen={isOpen}
        onClose={() => {
          setSuccessInfo(null);
          onSaved?.();
          onClose();
        }}
        title="Large Bond Request Submitted"
        size="sm"
        footer={
          <OathButton
            onClick={() => {
              setSuccessInfo(null);
              onSaved?.();
              onClose();
            }}
            className="w-full sm:w-auto sm:ml-auto"
          >
            OK
          </OathButton>
        }
      >
        <div className="text-left py-4">
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
            </svg>
          </div>
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Ref ID: {successInfo.refNumber}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Bankers Surety will contact you within 30 minutes. If you do not receive a call, please contact us at{' '}
            <a href="tel:877-778-5245" className="text-primary font-semibold hover:underline">877-778-5245</a>.
          </p>
        </div>
      </OathModal>
    );
  }

  return (
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      size="xl"
      footer={
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto sm:ml-auto">
          <OathButton
            variant="outline"
            onClick={handleClose}
            disabled={isSubmitting}
            className="w-full sm:w-auto"
          >
            Cancel
          </OathButton>
          <OathButton
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isLoading}
            className="w-full sm:w-auto"
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </OathButton>
        </div>
      }
    >
      {isLoading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <OathSpinner size="lg" />
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading bond details...</p>
        </div>
      ) : (
        <div className="space-y-6">
          {validationError && (
            <OathAlert variant="error" message={validationError} />
          )}

          {!validationError && areRequiredFieldsComplete(formState) && (
            <OathAlert variant="success" message="All required fields captured — ready to submit." />
          )}

          <BondInfoSection formState={formState} dispatch={dispatch} disabled={formDisabled} />

          <hr className="border-gray-200 dark:border-gray-700" />

          <DefendantSection formState={formState} dispatch={dispatch} disabled={formDisabled} />

          <hr className="border-gray-200 dark:border-gray-700" />

          <CitizenshipSection formState={formState} dispatch={dispatch} disabled={formDisabled} />

          <hr className="border-gray-200 dark:border-gray-700" />

          <OffenseSection formState={formState} dispatch={dispatch} disabled={formDisabled} />

          <hr className="border-gray-200 dark:border-gray-700" />

          <AdditionalDetailsSection
            formState={formState}
            dispatch={dispatch}
            existingDetails={existingDetails}
            disabled={formDisabled}
          />

          <hr className="border-gray-200 dark:border-gray-700" />

          <BondConditionsSection formState={formState} dispatch={dispatch} disabled={formDisabled} />

          <hr className="border-gray-200 dark:border-gray-700" />

          {/* Indemnitors */}
          <div>
            <h4 className="font-medium text-gray-900 dark:text-white mb-3">Indemnitor(s)</h4>
            <div className="space-y-4">
              {formState.indemnitors.map((ind, i) => (
                <IndemnitorSection
                  key={i}
                  indemnitor={ind}
                  index={i}
                  dispatch={dispatch}
                  onRemove={() => dispatch({ type: 'REMOVE_INDEMNITOR', index: i })}
                  canRemove={formState.indemnitors.length > 1}
                  disabled={formDisabled}
                  defendantAddress={defendantAddress}
                  isCollateralTypeTaken={isCollateralTypeTaken}
                />
              ))}
            </div>
            {!formDisabled && (
              <OathButton
                variant="outline"
                size="sm"
                onClick={() => dispatch({ type: 'ADD_INDEMNITOR' })}
                className="mt-3"
              >
                + Add Indemnitor
              </OathButton>
            )}
          </div>

          <hr className="border-gray-200 dark:border-gray-700" />

          <PowerInfoSection
            powers={formState.powers}
            dispatch={dispatch}
            bondId={isEdit ? bondId : null}
            isApproved={isPowerEligible}
            disabled={!isEdit || !isPowerEligible}
            agencyId={agencyId}
          />

          {/* Document upload for Pending bonds (no powers yet) */}
          {isEdit && isPending && bondId && (
            <BondDocumentUpload bondId={bondId} />
          )}
        </div>
      )}
    </OathModal>
  );
};

export default CreateLargeBondModal;
